import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import type {
  ArtistEntry,
  ArtistMember,
  FeaturedStatus,
} from './ArtistEditorModal';
import {
  DROPDOWN_EMPTY_CLASS,
  DROPDOWN_META_CLASS,
  DROPDOWN_OPTION_CLASS,
  DROPDOWN_PANEL_CLASS,
  FIELD_LABEL_CLASS,
  MODAL_ACTIONS_CLASS,
  SegmentedToggle,
  fieldControlClass,
} from './formStyles';

/**
 * Shared artist-editing building blocks — the single source of truth for the
 * "search / add / demote / remove an existing artist" rules that the Recording
 * Edit Form applies, so the mass-edit and bulk-edit surfaces behave identically.
 *
 * The edit form is the gold standard; these primitives were extracted from it:
 *   - {@link useArtistLookup} — debounced artist search (contract 4.4.1),
 *     returning full results (id / stageName / members) so links carry identity,
 *     not just a display string.
 *   - {@link FeaturedStatusToggle} — the Featured / Other Featured segmented
 *     control (fa / ofa) shown when adding an artist.
 *   - {@link ArtistSearchDropdown} — the search box + results dropdown that
 *     hands back the selected artist's identity ({@link SelectedArtist}); the
 *     caller then opens {@link FeaturedStatusModal} to choose featured status,
 *     exactly as the edit form does (search first, then a second modal for the
 *     featured/other-featured choice).
 *   - {@link FeaturedStatusModal} — the "Add artist" modal that captures the
 *     Featured / Other Featured choice for a just-selected artist and hands back
 *     a fully-formed {@link ArtistEntry}.
 *   - {@link ArtistActionModal} — the demote-vs-remove confirmation an existing
 *     artist chip opens (the edit form never hard-deletes a linked artist
 *     without offering "demote to non-featured" first).
 */

export interface ArtistLookupResult {
  id: string;
  stageName: string;
  members: ArtistMember[];
}

/**
 * Debounced artist lookup, mirroring RecordingEditForm's contract-4.4.1 search.
 * Only queries once the user has typed a non-empty term; requires a userId and
 * a parentRecordingId for the recording-scoped lookup endpoint.
 */
export function useArtistLookup(
  userId: string | undefined,
  parentRecordingId: string | undefined
): {
  query: string;
  setQuery: (q: string) => void;
  results: ArtistLookupResult[];
  loading: boolean;
} {
  const { adminApi: api } = useApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArtistLookupResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !parentRecordingId) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId,
          searchTerm: q,
        });
        setResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
            members: (a.members ?? []).map((m) => ({
              id: m.id ?? '',
              stageName: m.stageName ?? '',
            })),
          }))
        );
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, userId, parentRecordingId, api]);

  return { query, setQuery, results, loading };
}

/** Featured / Other Featured segmented toggle (fa / ofa). */
export const FeaturedStatusToggle: React.FC<{
  value: FeaturedStatus;
  onChange: (status: FeaturedStatus) => void;
}> = ({ value, onChange }) => (
  <SegmentedToggle
    leftLabel="Featured"
    rightLabel="Other Featured"
    isLeftSelected={value === 'fa'}
    onSelectLeft={() => onChange('fa')}
    onSelectRight={() => onChange('ofa')}
  />
);

/**
 * A just-selected artist's identity, before a featured status has been chosen.
 * Handed back by {@link ArtistSearchDropdown} and consumed by
 * {@link FeaturedStatusModal}.
 */
export interface SelectedArtist {
  name: string;
  id?: string;
  members?: ArtistMember[];
}

/**
 * Search box + results dropdown for linking an existing artist. On select it
 * hands back the artist's identity ({@link SelectedArtist}) — never a bare
 * display string — so the caller can open {@link FeaturedStatusModal} to choose
 * featured status, mirroring the edit form (search, then a second modal).
 */
export const ArtistSearchDropdown: React.FC<{
  userId?: string;
  parentRecordingId?: string;
  /** Names/ids already added — filtered out of the results. */
  existing: ArtistEntry[];
  onSelect: (artist: SelectedArtist) => void;
}> = ({ userId, parentRecordingId, existing, onSelect }) => {
  const { query, setQuery, results, loading } = useArtistLookup(
    userId,
    parentRecordingId
  );
  const [open, setOpen] = useState(false);

  const notAdded = results.filter(
    (a) => !existing.some((e) => e.name === a.stageName || e.id === a.id)
  );

  return (
    <div className="tw-relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search or select artist…"
        className={fieldControlClass()}
      />
      {open && (
        <div className={DROPDOWN_PANEL_CLASS}>
          {!query.trim() ? (
            <div className={DROPDOWN_EMPTY_CLASS}>
              Start typing to search for artists…
            </div>
          ) : loading ? (
            <div className={DROPDOWN_EMPTY_CLASS}>Searching...</div>
          ) : notAdded.length === 0 ? (
            <div className={DROPDOWN_EMPTY_CLASS}>
              {results.length === 0
                ? 'No matching artists'
                : 'All matching artists already added'}
            </div>
          ) : (
            notAdded.map((artist) => (
              <button
                key={artist.id || artist.stageName}
                type="button"
                className={DROPDOWN_OPTION_CLASS}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect({
                    name: artist.stageName,
                    id: artist.id || undefined,
                    members:
                      artist.members.length > 0 ? artist.members : undefined,
                  });
                  setQuery('');
                  setOpen(false);
                }}
              >
                <span>{artist.stageName}</span>
                {artist.id && (
                  <span className={DROPDOWN_META_CLASS}>{artist.id}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * "Add artist" modal — captures the Featured / Other Featured choice for an
 * artist the user just picked from {@link ArtistSearchDropdown}, then hands back
 * a fully-formed {@link ArtistEntry}. Mirrors the recording edit form, which
 * defers the featured choice to this second step rather than showing a toggle
 * inline with the search box.
 */
export const FeaturedStatusModal: React.FC<{
  pending: SelectedArtist | null;
  onCancel: () => void;
  onAdd: (entry: ArtistEntry) => void;
}> = ({ pending, onCancel, onAdd }) => {
  const [featured, setFeatured] = useState<FeaturedStatus>('fa');

  return (
    <WModal
      isOpen={pending !== null}
      onClose={() => {
        setFeatured('fa');
        onCancel();
      }}
      title="Add artist"
      modalClasses="tw-max-w-md tw-w-full tw-mx-4"
    >
      {pending !== null && (
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
              Artist
            </span>
            <p className="tw-text-white tw-text-sm">{pending.name}</p>
          </div>
          <div>
            <span className={`${FIELD_LABEL_CLASS} tw-mb-2`}>
              Featured Status
            </span>
            <FeaturedStatusToggle value={featured} onChange={setFeatured} />
          </div>
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setFeatured('fa');
                onCancel();
              }}
            />
            <WButton
              variant="primary"
              label="Add"
              onClick={() => {
                onAdd({
                  name: pending.name,
                  id: pending.id,
                  members:
                    pending.members && pending.members.length > 0
                      ? pending.members
                      : undefined,
                  isFeatured: featured === 'fa',
                  featuredStatus: featured,
                });
                setFeatured('fa');
              }}
            />
          </div>
        </div>
      )}
    </WModal>
  );
};

export type ArtistAction = 'demote' | 'remove';

/**
 * Demote-vs-remove confirmation for an existing (already-linked) artist —
 * mirrors the edit form. A pending-create artist that was never linked skips
 * this and is removed outright by the caller. Not shown for the last remaining
 * artist (a recording must keep at least one).
 */
export const ArtistActionModal: React.FC<{
  artistName: string | null;
  onCancel: () => void;
  onDemote: () => void;
  onRemove: () => void;
}> = ({ artistName, onCancel, onDemote, onRemove }) => {
  const [confirm, setConfirm] = useState<ArtistAction | null>(null);

  return (
    <WModal
      isOpen={artistName !== null}
      onClose={() => {
        setConfirm(null);
        onCancel();
      }}
      title={
        confirm === 'demote'
          ? 'Confirm Demote'
          : confirm === 'remove'
          ? 'Confirm Remove'
          : 'Artist Action'
      }
      modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
    >
      {artistName !== null &&
        (confirm === 'demote' ? (
          <div className="tw-mt-4 tw-space-y-4">
            <p className="tw-text-white tw-text-sm">
              Are you sure you want to demote <strong>{artistName}</strong> to
              Non-Featured?
            </p>
            <div className={MODAL_ACTIONS_CLASS}>
              <WButton
                variant="secondary"
                label="Cancel"
                onClick={() => setConfirm(null)}
              />
              <WButton
                variant="primary"
                label="Demote"
                onClick={() => {
                  setConfirm(null);
                  onDemote();
                }}
              />
            </div>
          </div>
        ) : confirm === 'remove' ? (
          <div className="tw-mt-4 tw-space-y-4">
            <p className="tw-text-white tw-text-sm">
              Are you sure you want to remove <strong>{artistName}</strong> from
              this recording?
            </p>
            <div className={MODAL_ACTIONS_CLASS}>
              <WButton
                variant="secondary"
                label="Cancel"
                onClick={() => setConfirm(null)}
              />
              <WButton
                variant="primary"
                label="Remove"
                onClick={() => {
                  setConfirm(null);
                  onRemove();
                }}
              />
            </div>
          </div>
        ) : (
          <div className="tw-mt-4 tw-space-y-3">
            <p className="tw-text-white tw-text-sm">
              What would you like to do with <strong>{artistName}</strong>?
            </p>
            <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end tw-pt-2">
              <WButton
                variant="secondary"
                label="Demote to Non-Featured"
                extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                onClick={() => setConfirm('demote')}
              />
              <WButton
                variant="secondary"
                label="Remove from Recording"
                extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                onClick={() => setConfirm('remove')}
              />
            </div>
          </div>
        ))}
    </WModal>
  );
};

/** Small chip shown for an artist that has been demoted to non-featured. */
export const DemotedArtistChip: React.FC<{
  name: string;
  onUndo?: () => void;
}> = ({ name, onUndo }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-surfaceBlack80 tw-border tw-border-dashed tw-border-textSeal tw-px-3 tw-py-1 tw-text-xs tw-text-textDove">
    {name}
    <span className="tw-text-textSeal tw-italic">demoted</span>
    {onUndo && (
      <button
        type="button"
        aria-label={`Undo demote ${name}`}
        className="tw-text-textDove hover:tw-text-white"
        onClick={onUndo}
      >
        <X className="tw-w-3 tw-h-3" />
      </button>
    )}
  </span>
);
